import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import GuestRoomSelector from './guestRoomSelector';  // DEPTH - 1 탑승인원 및 선실 선택
import GuestRoomBook from './guestRoomBook';          // DEPTH - 2 선실 위치 선택
import GuestWriteInfo from './guestWriteInfo';        // DEPTH - 3 예약자 정보 입력
import GuestPayment from './guestPayment';            // DEPTH - 4 결제
import GuestBookingResult from './guestBookingResult' // DEPTH - 5 결과
import '../../css/Booking/booking.css';
import CommonModal from '../../components/Common-components/Modal/modal';
import MainNoticePopup from '../../components/Home/MainNoticePopup';
import { ReactComponent as ArrowSVG } from '../../assets/icons/svg/arrowUnder.svg';
import { ReactComponent as LocationSVG } from '../../assets/icons/svg/location.svg';
import NavScheduleContent from '../../components/Common-components/NavigatorBar/NavigatorContents/navScheduleContent';
import { usePageTitle } from '../../contexts/PageTitleContext';
import { apiGet } from '../../utils/api';
import { buildCabinReselectionPayload } from '../../utils/cabinReselection';
import { formatBookingCalendarDate, getBookingEndpointTimes } from '../../utils/bookingScheduleTimes';
import Loading from '../../components/Common-components/loading';
import { useUser } from '../../contexts/UserContext';
import { getCreatedBookingEntry, rememberCreatedBooking } from '../../utils/createdBookingStorage';
import {
  buildBookingDeckInfoUrl,
  canBuildBookingDeckInfoRequest,
  getBookingGroupsCacheKey,
  isBookingBasketCacheKeyForSchedule,
  resolveTraveltekResultNo,
} from '../../utils/traveltekBookingApi';
import {
  BOOKING_SUBMISSION_POLICY,
  parseBookingSubmissionBlock,
} from '../../utils/traveltekBookingSubmissionPolicy';

/**
 * ───────── 20251024 Booking 전체 플로우 & 세션 스토리지 동기화 ─────────
 * 1) 진입 & 복원
 *    - URL 파라미터 idx(항차 id)를 기준으로 sessionStorage에 저장된 `booking:snapshot:${idx}` 를 읽어
 *      이전 진행 단계(stepHint)와 탑승객/객실 정보를 복원
 *    - `booking:sessionKey:*` 계열 키(항차/전역/크루즈 id)를 탐색하여 Traveltek 세션키를 재사용
 *    - PASSENGER_AGE_RULES, SENIOR_RULES를 line_id 기반으로 계산해 ageConfig로 하위 컴포넌트에 전달
 *
 * 2) 1단계 GuestRoomSelector
 *    - 탑승 인원수와 나이를 입력하면 `normalizeGuestCounts/normalizeGuestAges`로 가공및 시니어 규칙 검증을 수행
 *    - “예약 가능 선실 보기” 시 `onSubmitPax` payload를 만들어 snapshot(`booking:snapshot:${idx}`)에 저장
 *    - 그룹/요금 조회 결과는 `booking:groups:${idx}` 캐시에 유지하고, 세션키 갱신 시 `booking:sessionKeyIssuedAt:*`로 TTL을 관리한다.
 *    - 시니어 체크가 활성화되면 `adultDobs`, 소아는 `childrenDob`를 API 파라미터에 추가한다.
 *    - 선실 등급 선택 전까지 진행 -> 새로고침 및 다른 페이지 이동해도 복원하지 않도록 세팅
 *
 * 3) 객실 선택 이후(2단계 GuestRoomBook)
 *    - 선실/데크 선택 결과는 `bookingPayload.carbinGradeInfo`로 유지되고 snapshot에 반영
 *    - “선택하기” 시 `booking:basket:${idx}:*` 캐시를 생성하고, `booking:basketHold:${idx}` 에 홀드 시간(hold/release)을 저장
 *
 * 4) 3단계 GuestWriteInfo
 *    - `booking:basket:${idx}:…` 캐시를 파싱해 `cruiseBasketInfo`에 로드하며, basket이 비어 있거나 만료 상태면 `traveltek/add_cruise_basket.php`
 *      API를 다시 호출해 최신 요금/객실 정보 가져옴
 *    - `booking:basketHold:${idx}`에 저장된 hold/release 시각을 파싱해 타이머를 유지하고, 만료로 인해 한 번 정리된 뒤 재진입하면
 *      `booking:basketSkipFetch:${idx}` 플래그로 직전 정리 과정에서 발생한 중복 fetch를 한 번 건너뛴 뒤 플래그를 제거하여 최종적으로 API가 재호출
 *    - 승객 정보 formData 완료 시 bookingPayload 및 snapshot(`booking:snapshot:${idx}`)을 갱신
 *    - 이후 행동은 두 갈래:
 *        a. “예약 요청” : 보증금(hold)만 유지한 채 3단계를 마치고, 4단계 결제 없이 결과 페이지로 이동
 *        b. “결제하기” : 결제 수단 입력 단계(4단계)로 넘어가 최종 결제 프로세스를 진행
 *    - 3단계 까지 복원가능(탑승객 입력값 제외)
 *
 * 5) 4단계 GuestPayment → 5단계 GuestBookingResult
 *    - 카드 결제 폼은 카드 소유자 정보, 카드 번호/유효기간/생년월일/비밀번호 앞 2자리 등을 검증(카드 정보 npm 라이브러리 이용중)
 *      - PG 사 규격에 맞는 토큰을 생성한 뒤 결제 API를 호출예정
 *    - 결제 성공 시 `removeBookingSessionArtifacts`로 해당 항차의 sessionKey·snapshot·그룹/바스켓 캐시를 정리하고
 *      `booking:skipGroupsCache:${idx}`를 설정
 *
 * 6) 공통 정리 로직
 *    - `removeBookingSessionArtifacts(cruiseId, scheduleId)` : 주어진 항차/크루즈에 해당하는 sessionKey, snapshot,
 *      group 캐시, basket 캐시(`booking:basket:${scheduleId}:*` 또는 글로벌 prefix)를 정리
 *    - 홀드 만료 감지:
 *        a. GuestRoomSelector 단계 → `clearBasketArtifacts()` 가 `booking:basket:${idx}:*`, `booking:basketHold:${idx}` 삭제
 *        b. 주기적 세션 정리 → `booking:basketHold:*` 의 releaseAt이 현재보다 이하면 해당 항차 basket만 제거
 *        c. GuestWriteInfo 복원 과정에서도 releaseAt이 지난 경우 동일한 정리
 *    - 세션 만료 감지:
 *        해당 항차 세션 스토리지 정보 전부 삭제
 *    - 모든 정리 후에는 `booking:skipGroupsCache:${idx}` 를 1로 설정해 다음 접속 시 캐시를 무시하고 최신 데이터 받게
 *
 * 위 흐름을 통해 각 단계 컴포넌트가 동일한 sessionStorage 데이터 공유 및,
 * 항차별(/booking/idx)로 독립적인 상태(세션키, snapshot, basket, 홀드 타이머)를 유지/정리하도록 구성
 */

// 🔹 상세 응답 → 화면용 객체로 변환 (booking.js 내부에 정의)
function mapDetailToCruise(detail, fallbackId) {
  if (!detail) return null;

  const schedule = detail.schedule ?? {};
  const images   = detail.images ?? [];
  const codeToCruiseId = detail.schedule.codetocruiseid ?? null;

  // 대표 이미지: schedule.ship_main_image > images[0] > ''
  const image =
    schedule.small_image_url ||
    (images[0]?.url || images[0]?.image_url) ||
    '';
  
  // 년월일 형식 날짜
  const headerStartDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
  };

  // 제목: 선박명(국문/영문) + 일정명
  const shipName =
    schedule.ship_name_ko || schedule.ship_name_en || '';
  // const title =
  //   shipName && schedule.name ? `${shipName} · ${schedule.name}` :
  //   shipName || schedule.name || '크루즈';
  const title = (
    <>
      <span>{headerStartDate(schedule.saildate)} 출발</span>
      <span>{schedule.region_name} 크루즈 여행</span>
      <span>{schedule.nights}박 {Number(schedule.nights) + 1}일</span>
      <span>{shipName}</span>
    </>
  );

  // 날짜 ISO 정규화
  const toISO = (d) => {
    if (!d) return '';
    return /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00Z` : d;
  };

  const departureDate = toISO(schedule.saildate);
  const arrivalDate   = toISO(schedule.enddate);
  const { departureTime, arrivalTime } = getBookingEndpointTimes(detail);

  // 출발/도착지
  const departureLocation = schedule.departure_port_name || '';
  const arrivalLocation   = schedule.arrival_port_name || '';

  const roomType = [
    { typeName: '인사이드', lowPrice: Number(schedule.cruiseinside) || 0 },
    { typeName: '오션뷰',   lowPrice: Number(schedule.cruiseoutside) || 0 },
    { typeName: '발코니',   lowPrice: Number(schedule.cruisebalcony) || 0 },
    { typeName: '스위트',   lowPrice: Number(schedule.cruisesuite) || 0 },
  ].filter(rt => rt.lowPrice > 0);

  return {
    id: schedule.id ?? fallbackId ?? '',
    // code: schedule.codetocruiseid ?? schedule.voyagecode ?? String(fallbackId ?? ''),
    code: schedule.voyagecode ?? schedule.codetocruiseid ?? String(fallbackId ?? ''),
    shipName,
    title,
    image,
    departureDate,
    departureTime,
    departureLocation,
    departure_port_id: schedule.departure_port_id ?? null,
    arrivalDate,
    arrivalTime,
    arrivalLocation,
    roomType,
    codeToCruiseId: codeToCruiseId,
    line_id: schedule.line_id ?? null,
    lineId: schedule.line_id ?? null,
    ship_id: schedule.ship_id ?? null,
    shipId: schedule.ship_id ?? null,
  };
}

const MSC_LINE_ID = 16; //타 선사 기본값
const DISNEY_LINE_ID = 73;

const PASSENGER_AGE_RULES = {
  16: { lineId: 16, adultMinAge: 18, childMinAge: 1, childMaxAge: 17 }, // MSC
  17: { lineId: 17, adultMinAge: 18, childMinAge: 1, childMaxAge: 17 }, // NCL
  22: { lineId: 22, adultMinAge: 13, childMinAge: 1, childMaxAge: 12 }, // 로얄 캐리비안
  73: { lineId: 73, adultMinAge: 18, childMinAge: 1, childMaxAge: 17 }, // 디즈니
};

const SENIOR_RULES = {
  //MSC
  16: {
    type: 'all',
    minAge: 65,
    description: '같은 선실에 투숙하는 모든 승객이 만 65세 이상인 경우에만 시니어 할인이 적용됩니다. (일부 항차, 승선일 기준)',
  },
  // NCL
  17: {
    type: 'any',
    minAge: 55,
    description: '성인 중 1명이라도 만 55세 이상인 경우에 시니어 할인이 적용됩니다. (일부 항차, 승선일 기준)',
  },
  // 로얄 캐리비안
  22: {
    type: 'any',
    minAge: 55,
    description: '성인 중 1명이라도 만 55세 이상인 경우에 시니어 할인이 적용됩니다. (일부 항차, 승선일 기준)',
  },
  // 디즈니
  // 73: {
  //   type: 'any',
  //   minAge: 55,
  //   description: '성인 중 1명이라도 만 55세 이상인 경우에 시니어 할인이 적용됩니다. (일부 항차)',
  // },
};

// 세션키 삭제 관련
const DEFAULT_SESSION_TTL_MINUTES = 60;
// const DEFAULT_SESSION_TTL_MINUTES = 2; // 테스트 확인용
const SESSION_TTL_STORAGE_KEY = 'booking:sessionTTLMinutes';

const parsePositiveNumber = (value) => {
  if (value == null) return null;
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return num;
};

const resolveSessionTtlMs = () => {
  /**
   * TTL 우선순위:
   *  1) window.bookingSessionTTLMinutes / window.BOOKING_SESSION_TTL_MINUTES (실행 중 즉시 반영)
   *  2) sessionStorage.booking:sessionTTLMinutes (페이지 재방문 시 유지)
   *  3) 기본값 30분
   */
  try {
    const candidates = [];
    if (typeof window !== 'undefined') {
      candidates.push(window.bookingSessionTTLMinutes);
      candidates.push(window.BOOKING_SESSION_TTL_MINUTES);
    }
    if (typeof sessionStorage !== 'undefined') {
      candidates.push(sessionStorage.getItem(SESSION_TTL_STORAGE_KEY));
    }
    for (const candidate of candidates) {
      const parsed = parsePositiveNumber(candidate);
      if (parsed) {
        return parsed * 60 * 1000;
      }
    }
  } catch (_) {
    // ignore resolution errors
  }
  return DEFAULT_SESSION_TTL_MINUTES * 60 * 1000;
};

const removeBookingSessionArtifacts = (cruiseId, scheduleId) => {
  // 예약 제출 결과 확인/안전 초기화를 기다리는 항차는 일반 TTL·복원
  // 정리 경로에서 건드리지 않는다. 확정 실패 초기화는 소유권을 검증하는
  // removeScopedBookingSessionArtifacts 경로만 사용한다.
  if (scheduleId != null && hasStoredBookingSubmissionBlock(scheduleId)) {
    return false;
  }
  /**
   * 세션 키와 직접 연결된 항목만 선별 삭제
   * booking:basket:* 은 세션 키 기반이므로 모두 제거
   */
  let removed = false;
  let effectiveCruiseId = cruiseId;
  if (!effectiveCruiseId && scheduleId != null && typeof sessionStorage !== 'undefined') {
    try {
      // idx만 넘어오더라도 대응되는 cruiseId 키를 함께 제거할 수 있도록 매핑 검색
      effectiveCruiseId = sessionStorage.getItem(`booking:scheduleToCruiseId:${scheduleId}`) || null;
    } catch (_) { /* noop */ }
  }

  const keysToRemove = [
    'bookingSessionKey',
    'bookingSessionKeyIssuedAt',
  ];

  if (effectiveCruiseId) {
    keysToRemove.push(
      `booking:sessionKey:${effectiveCruiseId}`,
      `booking:sessionKeyIssuedAt:${effectiveCruiseId}`
    );
  }

  if (scheduleId) {
    // idx 스코프 자료(캐시, 매핑, TTL)를 삭제
    keysToRemove.push(
      `booking:sessionKey:${scheduleId}`,
      getBookingGroupsCacheKey(scheduleId),
      `booking:groups:${scheduleId}`,
      `booking:skipGroupsCache:${scheduleId}`,
      `booking:basketHold:${scheduleId}`,
      `booking:basketSkipFetch:${scheduleId}`,
      `booking:snapshot:${scheduleId}`,
      `booking:sessionKeyIssuedAt:${scheduleId}`,
      `booking:sessionKeyIssuedAt:schedule:${scheduleId}`,
      `booking:scheduleToCruiseId:${scheduleId}`
    );
  }

  try {
    keysToRemove.forEach(key => {
      try {
        if (sessionStorage.getItem(key) !== null) removed = true;
        sessionStorage.removeItem(key);
      } catch (_) { /* noop */ }
    });

    const len = sessionStorage.length;
    const toRemove = [];
    for (let i = 0; i < len; i++) {
      const key = sessionStorage.key(i);
      if (isBookingBasketCacheKeyForSchedule(key, scheduleId)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach(key => {
      try {
        removed = true;
        sessionStorage.removeItem(key);
      } catch (_) { /* noop */ }
    });
  } catch (_) {
    // ignore cleanup errors
  }

  return removed;
};

/**
 * 실제 제출 세션의 소유권을 확인한 뒤 한 항차만 정리하는 보수적 경로.
 * 확정 실패 후 재시작과 예약 완료 정리에 공통으로 사용한다.
 */
const removeScopedBookingSessionArtifacts = ({
  cruiseId,
  scheduleId,
  expectedSessionKey,
}) => {
  if (typeof sessionStorage === 'undefined' || scheduleId == null || !expectedSessionKey) {
    return { ok: false, reason: 'MISSING_RESET_CONTEXT' };
  }

  const scheduleKey = String(scheduleId);
  const expectedKey = String(expectedSessionKey);
  try {
    const storedCruiseId = sessionStorage.getItem(`booking:scheduleToCruiseId:${scheduleKey}`) || '';
    const effectiveCruiseId = String(cruiseId || storedCruiseId || '');
    const mappingMatches = Boolean(effectiveCruiseId && storedCruiseId === effectiveCruiseId);
    const scopedSessionKey = sessionStorage.getItem(`booking:sessionKey:${scheduleKey}`) || '';
    const cruiseSessionKey = effectiveCruiseId
      ? sessionStorage.getItem(`booking:sessionKey:${effectiveCruiseId}`) || ''
      : '';
    const globalSessionKey = sessionStorage.getItem('bookingSessionKey') || '';

    // 실패 이후 같은 항차에 새 세션이 발급됐다면 이전 실패를 근거로 지우지 않는다.
    if ((scopedSessionKey && scopedSessionKey !== expectedKey)
      || (cruiseSessionKey && cruiseSessionKey !== expectedKey)) {
      return { ok: false, reason: 'BOOKING_SESSION_CHANGED' };
    }
    // 적어도 한 위치에서 실제 실패 요청의 세션 소유권이 확인되어야 한다.
    if (![scopedSessionKey, cruiseSessionKey, globalSessionKey].includes(expectedKey)) {
      return { ok: false, reason: 'BOOKING_SESSION_OWNERSHIP_UNCONFIRMED' };
    }

    const keysToRemove = [
      `booking:sessionKey:${scheduleKey}`,
      getBookingGroupsCacheKey(scheduleKey),
      `booking:groups:${scheduleKey}`,
      `booking:skipGroupsCache:${scheduleKey}`,
      `booking:basketHold:${scheduleKey}`,
      `booking:basketSkipFetch:${scheduleKey}`,
      `booking:snapshot:${scheduleKey}`,
      `booking:sessionKeyIssuedAt:${scheduleKey}`,
      `booking:sessionKeyIssuedAt:schedule:${scheduleKey}`,
      `booking:scheduleToCruiseId:${scheduleKey}`,
    ].filter(Boolean);

    if (effectiveCruiseId && (cruiseSessionKey === expectedKey || (mappingMatches && !cruiseSessionKey))) {
      keysToRemove.push(
        `booking:sessionKey:${effectiveCruiseId}`,
        `booking:sessionKeyIssuedAt:${effectiveCruiseId}`,
      );
    }

    // legacy 전역 키는 실패한 요청의 세션과 정확히 일치할 때만 현재 항차 소유로 본다.
    if (globalSessionKey === expectedKey) {
      keysToRemove.push('bookingSessionKey', 'bookingSessionKeyIssuedAt');
    }

    const basketKeys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (isBookingBasketCacheKeyForSchedule(key, scheduleKey)) basketKeys.push(key);
    }
    keysToRemove.push(...basketKeys);

    const uniqueKeys = [...new Set(keysToRemove)];
    uniqueKeys.forEach(key => sessionStorage.removeItem(key));

    const remainingKeys = uniqueKeys.filter(key => sessionStorage.getItem(key) !== null);
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (isBookingBasketCacheKeyForSchedule(key, scheduleKey)) remainingKeys.push(key);
    }

    if (remainingKeys.length > 0) {
      return { ok: false, reason: 'BOOKING_STORAGE_CLEANUP_INCOMPLETE', remainingKeys };
    }
    return { ok: true };
  } catch (_) {
    return { ok: false, reason: 'BOOKING_STORAGE_CLEANUP_FAILED' };
  }
};

function getPassengerAgeConfig(lineId) {
  const numericLineId = Number(lineId);
  const rule = PASSENGER_AGE_RULES[numericLineId] ?? PASSENGER_AGE_RULES[MSC_LINE_ID];
  const resolvedLineId = rule.lineId;
  const seniorRule = SENIOR_RULES[numericLineId] ?? null;
  return {
    sourceLineId: Number.isFinite(numericLineId) ? numericLineId : null,
    lineId: resolvedLineId,
    adult: {
      minAge: rule.adultMinAge,
      label: `성인(만 ${rule.adultMinAge}세 이상)`,
      summaryLabel: '성인',
    },
    child: {
      minAge: rule.childMinAge,
      maxAge: rule.childMaxAge,
      label: `소아(만 ${rule.childMinAge}-${rule.childMaxAge}세)`,
      summaryLabel: '소아',
    },
    senior: seniorRule,
  };
}

function normalizeCounts(rawCounts) {
  if (!rawCounts || typeof rawCounts !== 'object') return null;
  const adults = Number(rawCounts.adults) || 0;
  const children = Number(rawCounts.children) || 0;
  return { adults, children };
}

function normalizeAges(rawAges) {
  if (!rawAges || typeof rawAges !== 'object') return null;
  const childAges = Array.isArray(rawAges.children) ? [...rawAges.children] : [];
  return { children: childAges };
}

function normalizePastPax(rawPastPax) {
  return String(rawPastPax || '').trim();
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const normalized = String(value).trim();
    if (normalized) return normalized;
  }
  return '';
}

function normalizeMembershipGender(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (['m', 'male', 'mr', 'sir'].includes(normalized)) return 'male';
  if (['f', 'female', 'ms', 'mrs', 'miss', 'madam'].includes(normalized)) return 'female';
  return '';
}

function normalizeMembershipPassengerInfo(value) {
  if (!value || typeof value !== 'object') return null;
  const firstName = firstNonEmpty(value.firstName, value.firstname, value.first_name, value.forename, value.givenname);
  const lastName = firstNonEmpty(value.lastName, value.lastname, value.last_name, value.surname, value.familyname);
  const birthDate = firstNonEmpty(value.birthDate, value.dob, value.dateofbirth, value.date_of_birth);
  const gender = normalizeMembershipGender(firstNonEmpty(value.gender, value.sex, value.title));
  if (!firstName && !lastName && !birthDate && !gender) return null;
  return { firstName, lastName, birthDate, gender };
}

// idx/크루즈 별 세션키를 우선 탐색 복원/호출 참조
const resolveScheduleScopedSessionKey = (cruiseId, scheduleId) => {
  if (typeof sessionStorage === 'undefined') return null;
  const candidates = [];
  if (scheduleId != null) candidates.push(`booking:sessionKey:${scheduleId}`);
  if (cruiseId) candidates.push(`booking:sessionKey:${cruiseId}`);
  candidates.push('bookingSessionKey'); // legacy fallback
  for (const key of candidates) {
    try {
      const value = sessionStorage.getItem(key);
      if (value) return value;
    } catch (_) { /* noop */ }
  }
  return null;
};

const clampFloatingPosition = (position, size, margin = 32) => {
  if (typeof window === 'undefined') return position;
  const safeMargin = Number.isFinite(margin) ? margin : 32;
  const width = Number(size?.width) || 0;
  const height = Number(size?.height) || 0;
  const maxX = Math.max((window.innerWidth || 0) - width - safeMargin, safeMargin);
  const maxY = Math.max((window.innerHeight || 0) - height - safeMargin, safeMargin);
  const clampedX = Math.min(Math.max(position.x, safeMargin), maxX);
  const clampedY = Math.min(Math.max(position.y, safeMargin), maxY);
  return { x: clampedX, y: clampedY };
};

// 세션스토리지에 저장된 객실 홀드 정보를 idx 기준으로 읽어온다
const readBasketHoldInfo = (scheduleId) => {
  if (typeof sessionStorage === 'undefined' || scheduleId == null) return null;
  try {
    const raw = sessionStorage.getItem(`booking:basketHold:${scheduleId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.releaseAt) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

// book.pl 호출 결과가 확정되지 않았거나 확정 실패 후 안전한 초기화를
// 기다리는 항차는 TTL/홀드 정리 대상에서 제외한다. 손상된 차단 기록도
// 수동 확인 상태로 해석해 세션 소유권을 임의로 제거하지 않는다.
const hasStoredBookingSubmissionBlock = (scheduleId) => {
  if (typeof sessionStorage === 'undefined' || scheduleId == null) return false;
  try {
    return Boolean(parseBookingSubmissionBlock(
      sessionStorage.getItem(`booking:submissionBlocked:${scheduleId}`)
    ));
  } catch (_) {
    return true;
  }
};

export default function Booking() {
  const { sessionReady, bookingStorageReady, bookingAccountEpoch } = useUser();
  const { idx } = useParams();
  const location = useLocation();
  const entryRef = useRef(null);
  const ready = sessionReady !== false && bookingStorageReady !== false;
  const entryKey = `${bookingAccountEpoch || 0}:${idx}`;
  if (ready && entryRef.current?.key !== entryKey) {
    entryRef.current = { key: entryKey, recovery: getCreatedBookingEntry(idx, location.state) };
  }
  const recovery = ready ? entryRef.current?.recovery : null;
  useEffect(() => {
    if (!recovery?.legacyCreated) return;
    // Older builds kept a created snapshot and the consumed session until payment.
    // Retire only this schedule's owned session; keep other sessions and review blocks.
    try {
      const cruiseId = sessionStorage.getItem(`booking:scheduleToCruiseId:${idx}`) || '';
      const expectedSessionKey = sessionStorage.getItem(`booking:sessionKey:${idx}`)
        || (cruiseId && sessionStorage.getItem(`booking:sessionKey:${cruiseId}`)) || '';
      const result = expectedSessionKey
        ? removeScopedBookingSessionArtifacts({ scheduleId: idx, cruiseId, expectedSessionKey })
        : { ok: true };
      if (result.ok) {
        sessionStorage.removeItem(`booking:snapshot:${idx}`);
        sessionStorage.removeItem(getBookingGroupsCacheKey(String(idx)));
        sessionStorage.removeItem(`booking:groups:${idx}`);
        sessionStorage.setItem(`booking:skipGroupsCache:${idx}`, '1');
      }
    } catch (_) { /* server replay protection remains authoritative */ }
  }, [idx, recovery]);
  if (sessionReady === false) return <Loading />;
  if (bookingStorageReady === false) return <p role="alert">예약 정보를 안전하게 불러오지 못했습니다. 페이지를 새로고침해주세요.</p>;
  if (recovery) return <Navigate to={recovery.path} replace />;
  return <BookingFlow key={bookingAccountEpoch || 0} />;
}

function BookingFlow() {
  const { idx } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // const [currentStep, setCurrentStep] = useState(1); // 초기 렌더링 depth 수정으로 인해 주석 후 수정
  // 초기 렌더에서 step 깜빡임 방지: location.state 또는 스냅샷 hint로 초기값 설정
  const resolveInitialStep = () => {
    try {
      const raw = Number(location?.state?.step);
      let init = Number.isFinite(raw) && raw >= 0 && raw <= 5 ? raw : undefined;
      if (init == null) {
        const snapStr = sessionStorage.getItem(`booking:snapshot:${idx}`);
        if (snapStr) {
          const snap = JSON.parse(snapStr);
          const hint = Number(snap?.stepHint);
          if (Number.isFinite(hint) && hint >= 0 && hint <= 5) init = hint;
        }
      }
      if (!Number.isFinite(init)) return 0;
      return init >= 2 ? init : 0;
    } catch (_) {
      return 0; // 기본 depth-0 (1단계 1-1)
    }
  };

  const [currentStep, setCurrentStep] = useState(resolveInitialStep);

  const [isRestoring, setIsRestoring] = useState(false); // 이전에 예약 진행했던 정보 불러오는 용도
  const [detailData, setDetailData] = useState(null);
  const [bookingPayload, setBookingPayload] = useState(null); // 1단계 payload
  const reservationType = bookingPayload?.reservationType ?? false;
  const hasReservationResult = !!bookingPayload?.passengerInfo?.bookingResult;
  const hasPaymentResult = !!bookingPayload?.paymentResult;
  const hasCreatedReservation = !!bookingPayload?.reservationCreated || hasReservationResult;
  // 같은 탭의 sessionStorage 변경은 storage 이벤트를 발생시키지 않으므로
  // 자식의 변경 알림으로 부모 타이머 판정을 즉시 다시 계산한다.
  const currentScheduleKey = idx == null ? '' : String(idx);
  const [bookingSubmissionBlockState, setBookingSubmissionBlockState] = useState(() => ({
    scheduleId: currentScheduleKey,
    blocked: hasStoredBookingSubmissionBlock(idx),
  }));
  // URL의 idx가 바뀐 첫 렌더에서는 이전 항차 state를 재사용하지 않고 새
  // 항차의 storage를 즉시 읽는다.
  const hasBookingSubmissionBlock = bookingSubmissionBlockState.scheduleId === currentScheduleKey
    ? bookingSubmissionBlockState.blocked
    : hasStoredBookingSubmissionBlock(idx);
  const handleBookingSubmissionBlockChange = useCallback(() => {
    setBookingSubmissionBlockState({
      scheduleId: currentScheduleKey,
      blocked: hasStoredBookingSubmissionBlock(idx),
    });
  }, [currentScheduleKey, idx]);
  const shouldLockPaymentStep = reservationType === true && hasCreatedReservation && !hasPaymentResult;
  const shouldFinalizeReservationTimers = hasCreatedReservation || hasPaymentResult;
  const shouldStopReservationTimers = shouldFinalizeReservationTimers || hasBookingSubmissionBlock;
  const { user, loading } = useUser();
  const { setPageTitle } = usePageTitle();
  const [notFound, setNotFound] = useState(false);
  const [cruise, setCruise] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionTimeLeftMs, setSessionTimeLeftMs] = useState(null);
  const [basketHoldInfo, setBasketHoldInfo] = useState(() => readBasketHoldInfo(idx)); // 세션에 남아 있는 홀드 정보를 복구
  const [basketHoldCountdown, setBasketHoldCountdown] = useState(null); // UI에 표시할 홀드 타이머 라벨
  const basketHoldAlertShownRef = useRef(false); // 만료 알림 중복 방지 플래그
  const snapshotRestorePromptShownRef = useRef(false); // 복원 안내 알림 중복 노출 방지
  const [sessionIndicatorPosition, setSessionIndicatorPosition] = useState(() => {
    if (typeof window !== 'undefined') {
      // 초기에는 충분히 큰 값을 넣고, 마운트 후 clamp 로직이 실제 폭에 맞춰 오른쪽 끝으로 스냅되게 한다
      const fallbackX = Math.max(window.innerWidth - (window.innerWidth <= 651 ? 72 : 200 ) - 40, 40); //(window.innerWidth - 대략적인 wrapper 크기 - 16 --> 우측 여백, 16 --> 앞쪽 계산이 40보다 작아져도 최소한으로 40은 보장
      const fallbackY = window.innerWidth <= 651 ? 24 : 120; // 모바일일 때는 조금 더 위에 배치 (web : 120 || mo : 72(이미지 렌더링 위치값) )
      return { x: fallbackX, y: fallbackY };
    }
    return { x: 24, y: 120 };
  });
  const passengerAgeConfig = useMemo(
    () => getPassengerAgeConfig(detailData?.schedule?.line_id),
    [detailData?.schedule?.line_id]
  );
  const sessionCountdownLabel = useMemo(() => {
    if (sessionTimeLeftMs == null) return null;
    const remainingMs = Number(sessionTimeLeftMs);
    if (!Number.isFinite(remainingMs)) return null;
    const clampedMs = Math.max(remainingMs, 0);
    const totalSeconds = Math.floor(clampedMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');
    if (hours > 0) {
      return `${hours}:${mm}:${ss}`;
    }
    return `${mm}:${ss}`;
  }, [sessionTimeLeftMs]);
  const isSessionExpired = sessionTimeLeftMs != null && Number(sessionTimeLeftMs) <= 0;
  const isSessionCountdownCritical = sessionTimeLeftMs != null && Number(sessionTimeLeftMs) <= 60_000;
  const isHoldCountdownCritical = useMemo(() => {
    if (!basketHoldInfo?.releaseAt) return false;
    const releaseMs = new Date(basketHoldInfo.releaseAt).getTime();
    if (!Number.isFinite(releaseMs)) return false;
    return releaseMs - Date.now() <= 60_000 && (basketHoldCountdown ?? '') !== '00:00';
  }, [basketHoldInfo?.releaseAt, basketHoldCountdown]);
  const isDisneyBooking = Number(detailData?.schedule?.line_id ?? cruise?.line_id ?? cruise?.lineId) === DISNEY_LINE_ID;
  const sessionIndicatorRef = useRef(null);
  const sessionIndicatorDragRef = useRef({ isDragging: false, offsetX: 0, offsetY: 0, pointerId: null });
  const sessionIndicatorSizeRef = useRef({ width: 145, height: 44 });
  const clampIndicatorPosition = (position) => clampFloatingPosition(position, sessionIndicatorSizeRef.current);

  useEffect(() => {
    if (!shouldFinalizeReservationTimers) return;
    if (idx != null) {
      try { sessionStorage.removeItem(`booking:basketHold:${idx}`); } catch (_) { /* noop */ }
      window.dispatchEvent(new CustomEvent('basketHoldUpdated', { detail: { idx } }));
    }
    setSessionTimeLeftMs(null);
    setBasketHoldInfo(null);
    setBasketHoldCountdown(null);
  }, [shouldFinalizeReservationTimers, idx]);

  /** ---------- 단계 추적 로깅 (미사용 시 제거) 시작 ---------- */
  const stepChangeSourceRef = useRef('초기');
  const lastStepRef = useRef(null);
  const historyIndexRef = useRef(
    typeof location?.state?.historyIndex === 'number' ? location.state.historyIndex : 0
  );
  const nextHistoryIndexRef = useRef(historyIndexRef.current + 1);
  const isUnmountedRef = useRef(false);
  const prevIdxRef = useRef(idx);
  const sessionCleanupAlertShownRef = useRef(false);
  const enforcedLoginDowngradeRef = useRef(false);

  const markStepChange = (source) => {
    stepChangeSourceRef.current = source;
  };

  const pushStepState = (step) => {
    const historyIndex = nextHistoryIndexRef.current++;
    navigate('.', { state: { step, historyIndex } });
    historyIndexRef.current = historyIndex;
    if (nextHistoryIndexRef.current <= historyIndex) {
      nextHistoryIndexRef.current = historyIndex + 1;
    }
  };

  const replaceStepState = (step) => {
    const historyIndex = historyIndexRef.current;
    navigate('.', { state: { step, historyIndex }, replace: true });
    historyIndexRef.current = historyIndex;
    if (nextHistoryIndexRef.current <= historyIndex) {
      nextHistoryIndexRef.current = historyIndex + 1;
    }
  };

  const restoreFromSnapshot = useCallback(async ({
    skipStepSwitch = false,
    forceStep,
    dropCabinSelection = false,
    snapOverride = null,
  } = {}) => {
    if (!cruise || !detailData) return null;
    const storageKey = `booking:snapshot:${idx}`;
    let snap = snapOverride;
    if (!snap) {
      const rawSnap = sessionStorage.getItem(storageKey);
      if (!rawSnap) return null;
      try {
        snap = JSON.parse(rawSnap);
      } catch (_) {
        sessionStorage.removeItem(storageKey);
        return null;
      }
    }

    const counts = normalizeCounts(snap?.counts);
    const ages = normalizeAges(snap?.ages);
    const includeSenior = Object.prototype.hasOwnProperty.call(snap || {}, 'includeSenior')
      ? snap.includeSenior
      : (Object.prototype.hasOwnProperty.call(snap || {}, 'seniorStatus')
        ? snap.seniorStatus
        : null);
    const pastPax = normalizePastPax(snap?.pastPax);
    const membershipPassengerInfo = normalizeMembershipPassengerInfo(snap?.membershipPassengerInfo);
    const sel = snap?.selectedRef;
    const cabinRef = dropCabinSelection ? null : snap?.cabinRef;
    const stepHintValue = Number(snap?.stepHint);
    const stepHint = Number.isFinite(stepHintValue) ? stepHintValue : 1;
    const targetStep = forceStep ?? Math.max(2, Math.min(5, stepHint));

    const executeRestore = async () => {
      let nextPayload = {
        cruise,
        detailData,
        reservationType,
        reservationCreated: !!snap?.reservationCreated,
      };

      try {
        sessionStorage.removeItem(`booking:skipGroupsCache:${idx}`);
      } catch (_) { /* noop */ }

      if (counts) {
        nextPayload = { ...nextPayload, counts };
        if (ages) nextPayload = { ...nextPayload, ages };
      }

      if (includeSenior !== null) {
        nextPayload = { ...nextPayload, includeSenior };
      }
      nextPayload = { ...nextPayload, pastPax };
      if (membershipPassengerInfo) {
        nextPayload = { ...nextPayload, membershipPassengerInfo };
      }

      // 기존 선택(gradeno/ratecode 등)이 있으면 최신 deck 정보를 다시 받아온다.
      let deckPromise = null;
      if (canBuildBookingDeckInfoRequest({ gradeno: sel?.gradeno, ratecode: sel?.ratecode, resultno: sel?.resultno })) {
        const sessionKey = resolveScheduleScopedSessionKey(cruise.codeToCruiseId, idx) || '';
        const totalChildren = counts?.children || 0;
        const url = buildBookingDeckInfoUrl({
          sessionKey,
          cruiseId: String(cruise.codeToCruiseId || ''),
          gradeno: sel.gradeno,
          resultno: sel.resultno,
          adults: counts?.adults || 0,
          childrenTotal: totalChildren,
          ratecode: sel.ratecode,
        });

        // fetch 동안 글로벌 로딩을 노출하기 위해 isRestoring 토글
        setIsRestoring(true);
        deckPromise = apiGet(url)
          .catch(err => {
            console.error('deck info restore failed', err);
            return null;
          });
      }

      if (deckPromise) {
        if (skipStepSwitch) setIsRestoring(true);
        const deckRes = await deckPromise;
        const deckInfo = deckRes?.results ?? null;
        const carbinGradeInfo = {
          deckInfo,
          deckImageUrl: sel.deckImageUrl || '',
          grade_name: sel.grade_name || '',
          grade_name_ko: sel.grade_name_ko || '',
          cabincode: sel.cabincode || '',
          resultno: sel.resultno,
          gridPrice: { ratecode: sel.ratecode, gradeno: sel.gradeno },
          ...(sel.cabinMeta ? { cabinMeta: sel.cabinMeta } : {}),
        };
        nextPayload = { ...nextPayload, carbinGradeInfo };
      }

      // dropCabinSelection=true 이면 기존 선실 선택을 강제로 제거한다.
      if (cabinRef && !dropCabinSelection) {
        nextPayload = { ...nextPayload, userSelectCabinInfo: cabinRef };
      }

      if (!isUnmountedRef.current) {
        setBookingPayload(prev => {
          if (dropCabinSelection && prev) {
            const { userSelectCabinInfo, passengerInfo, paymentResult, ...restPrev } = prev;
            return { ...restPrev, ...nextPayload };
          }
          return { ...(prev || {}), ...nextPayload };
        });
      }

      if (deckPromise && skipStepSwitch) {
        setIsRestoring(false);
      }

      return { snap, targetStep };
    };

    if (skipStepSwitch) {
      return executeRestore();
    }

    markStepChange('restoreConfirm');
    replaceStepState(targetStep);
    if (isUnmountedRef.current) return { snap, targetStep };
    setCurrentStep(targetStep);
    setIsRestoring(true);

    try {
      return await executeRestore();
    } finally {
      if (!isUnmountedRef.current) {
        setIsRestoring(false);
      }
    }
  }, [cruise, detailData, reservationType, idx, replaceStepState, markStepChange, setBookingPayload]);

  // 비로그인 상태에서 복원 시, 남아 있는 홀드/바스켓 자료를 정리해 재요청을 유도
  const clearHoldArtifactsForSchedule = useCallback((scheduleId) => {
    if (typeof sessionStorage === 'undefined' || scheduleId == null) return;
    if (hasStoredBookingSubmissionBlock(scheduleId)) return;
    // booking:basket:${scheduleId}:* 키 목록을 모아 삭제
    // 스케줄별 바스켓 키 정리
    try {
      const removableKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (isBookingBasketCacheKeyForSchedule(key, scheduleId)) removableKeys.push(key);
      }
      removableKeys.forEach(key => {
        try { sessionStorage.removeItem(key); } catch (_) { /* noop */ }
      });
    } catch (_) { /* noop */ }
    // 홀드/skip 플래그 제거
    try { sessionStorage.removeItem(`booking:basketHold:${scheduleId}`); } catch (_) { /* noop */ } // 홀드 정보 초기화
    try { sessionStorage.removeItem(`booking:basketSkipFetch:${scheduleId}`); } catch (_) { /* noop */ } // skip 플래그 초기화
    // 타이머 표시 갱신 알림
    try {
      window.dispatchEvent(new CustomEvent('basketHoldUpdated', { detail: { idx: scheduleId } })); // 타이머 표시 즉시 갱신
    } catch (_) { /* noop */ }
  }, []);

  // 스냅샷을 강제로 2단계 상태로 낮춰 비로그인 사용자는 passenger 정보 없이 선실 선택 화면부터 다시 진행
  const downgradeSnapshotToStepTwo = useCallback((scheduleId) => {
    if (typeof sessionStorage === 'undefined' || scheduleId == null) return;
    const snapKey = `booking:snapshot:${scheduleId}`;
    const snapStr = sessionStorage.getItem(snapKey);
    if (!snapStr) return;
    try {
      const snap = JSON.parse(snapStr); // 기존 스냅샷 파싱
      const nextSnap = {
        ...snap,
        stepHint: 2,
        ts: Date.now(),
      };
      delete nextSnap.cabinRef; // 선실 고정 해제
      delete nextSnap.passengerInfo; // 탑승객 입력 제거
      sessionStorage.setItem(snapKey, JSON.stringify(nextSnap)); // 갱신 스냅샷 저장
    } catch (_) {
      try { sessionStorage.removeItem(snapKey); } catch (_) { /* noop */ } // 파싱 실패 시 스냅샷 제거
    }
  }, []);
  /** ---------- 단계 추적 로깅 (미사용 시 제거) 끝 ---------- */

  // depth 순서 (페이지 제목으로 사용)
  const depths = [
    { number: "①", name: "탑승 인원 및 선실 선택" }, // 탑승인원 지정
    { number: "①", name: "탑승 인원 및 선실 선택" }, // 선실 선택
    { number: "②", name: "선실 위치 선택" },
    { number: "③", name: "탑승객 정보 입력" },
    { number: "④", name: "결제하기" },
    { number: "⑤", name: "예약완료" },
  ];

  const headerGroups = [
    {
      steps: [0, 1],
      number: depths[0].number,
      name: depths[0].name,
    },
    { steps: [2], number: depths[2].number, name: depths[2].name },
    { steps: [3], number: depths[3].number, name: depths[3].name },
    { steps: [4], number: depths[4].number, name: depths[4].name },
    { steps: [5], number: depths[5].number, name: depths[5].name },
  ];

  // 세션키는 리로드 복구를 위해 유지 (기존: 무조건 삭제 → 제거)
  // useEffect(() => {
  //   sessionStorage.removeItem('bookingSessionKey');
  // }, []);

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  // 항차(idx)가 변경될 때 상태 초기화 및 단계 재계산
  useEffect(() => {
    if (prevIdxRef.current === idx) return;
    prevIdxRef.current = idx;

    stepChangeSourceRef.current = 'idxChange';
    setIsRestoring(false);
    setBookingPayload(null);
    setDetailData(null);
    setCruise(null);
    setNotFound(false);

    historyIndexRef.current = typeof location?.state?.historyIndex === 'number'
      ? location.state.historyIndex
      : 0;
    nextHistoryIndexRef.current = historyIndexRef.current + 1;

    setCurrentStep(resolveInitialStep());
  }, [idx, location?.state?.historyIndex, location?.state?.step]);

  /** ---------- 단계 추적 로깅 ---------- */
  useEffect(() => {
    window.scrollTo(0, 0);
    // currentStep에 따라 페이지 제목 설정
    if (depths[currentStep]) {
      setPageTitle(depths[currentStep].name);
    }
  }, [currentStep, setPageTitle]);

  useEffect(() => {
    if (typeof location?.state?.historyIndex !== 'number') {
      replaceStepState(currentStep);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handlePopState = (event) => {
      const prevIdx = historyIndexRef.current;
      const nextIdx = typeof event.state?.historyIndex === 'number' ? event.state.historyIndex : prevIdx;
      if (nextIdx < prevIdx) markStepChange('뒤로가기');
      else if (nextIdx > prevIdx) markStepChange('앞으로가기');
      else markStepChange('브라우저');
      historyIndexRef.current = nextIdx;
      if (nextHistoryIndexRef.current <= nextIdx) nextHistoryIndexRef.current = nextIdx + 1;
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (lastStepRef.current === null) {
      lastStepRef.current = currentStep;
      return;
    }
    if (lastStepRef.current === currentStep) return;
    const reason = stepChangeSourceRef.current || '알수없음';
    const rawStateStep = location?.state?.step;
    lastStepRef.current = currentStep;
    stepChangeSourceRef.current = '알수없음';
  }, [currentStep, location]);

  useEffect(() => {
    const idx = typeof location?.state?.historyIndex === 'number'
      ? location.state.historyIndex
      : historyIndexRef.current;
    historyIndexRef.current = idx;
    if (nextHistoryIndexRef.current <= idx) {
      nextHistoryIndexRef.current = idx + 1;
    }
  }, [location?.state?.historyIndex]);
  /** ---------- 단계 추적 로깅 ---------- */

  // 히스토리 state의 step <-> 내부 currentStep 동기화 (쿼리파라미터 제거 버전)
  useEffect(() => {
    if (isRestoring) return;
    // history state에서 step 읽기
    const raw = Number(location?.state?.step);
    let fromState = Number.isFinite(raw) && raw >= 0 && raw <= 5 ? raw : undefined;
    let usedSnapshot = false;

    // 세션 스냅샷(stepHint) 폴백
    if (fromState == null) {
      try {
        const snapStr = sessionStorage.getItem(`booking:snapshot:${idx}`);
        if (snapStr) {
          const snap = JSON.parse(snapStr);
          const hint = Number(snap?.stepHint);
          if (Number.isFinite(hint) && hint >= 0 && hint <= 5) {
            fromState = hint;
            usedSnapshot = true;
          }
        }
      } catch (_) { /* noop */ }
    }
    if (fromState == null) fromState = 0;

    // 앞단계 완료 여부 가드
    let safeStep = fromState;
    // 예약/결제 결과가 존재하면 항상 완료 단계로 고정 (이전 단계 진입 불가)
    const isCompleted = reservationType
      ? !!bookingPayload?.paymentResult
      : !!(bookingPayload?.paymentResult || bookingPayload?.passengerInfo?.bookingResult);
    if (isCompleted) {
      safeStep = 5;
    } else if (shouldLockPaymentStep && safeStep < 4) {
      // book.pl 예약 생성 이후 결제 전에는 승객 입력 단계로 되돌아갈 수 없다.
      safeStep = 4;
    }
    // 결제 비활성 모드에서는 step4 접근 불가
    if (reservationType === false && safeStep === 4) {
      // 예약요청 완료가 있으면 5로, 아니면 3으로 보정
      if (bookingPayload?.passengerInfo?.bookingResult || bookingPayload?.paymentResult) safeStep = 5;
      else safeStep = 3;
    }
    // 스냅샷 기반으로 depth-1 -> 에서 복구 진행
    const isRehydrating = usedSnapshot && !bookingPayload;
    if (!isRehydrating) {
      // step1은 탑승 인원 정보 필요
      if (safeStep >= 1 && !bookingPayload?.counts) safeStep = 0;
      // step2는 step1에서 선실 등급 선택 필요
      if (safeStep >= 2 && !bookingPayload?.carbinGradeInfo) safeStep = 1;
      // step3는 step2에서 객실 선택 필요
      if (safeStep >= 3 && !bookingPayload?.userSelectCabinInfo) safeStep = 2;
      // step4는 step3에서 탑승객 정보 필요
      if (safeStep >= 4 && !bookingPayload?.passengerInfo) safeStep = 3;
    }
    // step5는 결제/예약 결과 필요 (paymentResult 또는 bookingResult)
    if (safeStep >= 5 && !isCompleted) safeStep = 4;
    // 예약 요청이 진행 중이거나 결과 확인이 필요한 동안에는 같은 세션의
    // 이전 단계로 돌아가 선택값을 바꾸지 못하게 3단계에 고정한다.
    if (hasBookingSubmissionBlock && bookingPayload?.userSelectCabinInfo && !isCompleted) {
      safeStep = 3;
    }

    if (currentStep !== safeStep) {
      setCurrentStep(safeStep);
    }

    // rehydrating 동안에는 step을 강제로 낮추는 replace를 생략하고, 힌트(세션스토리지) 상태를 우선 유지
    if (!isRehydrating) {
      if (fromState !== safeStep) {
        markStepChange('guardAdjust');
        replaceStepState(safeStep);
      }
    } else {
      // state가 비어있다면 힌트만 반영
      if (location?.state?.step !== fromState) {
        markStepChange('rehydrateHint');
        replaceStepState(fromState);
      }
    }
  }, [location, bookingPayload, reservationType, navigate, idx, isRestoring, shouldLockPaymentStep, hasBookingSubmissionBlock]);

  // step5(예약완료)에서 브라우저 뒤로가기 시, booking 내부 단계로 가지 않고
  // booking 진입 이전의 페이지로 연속 이동 (booking 히스토리를 모두 건너뜀)
  useEffect(() => {
    if (currentStep !== 5) return;
    const handlePop = () => {
      const path = window.location.pathname || '';
      if (path.includes('/booking/')) {
        // 이전 히스토리가 여전히 booking이면 한 단계 더 뒤로가기
        window.history.back();
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [currentStep]);

  // book.pl 성공이 확인되면 결제 전이라도 실제 제출한 세션을 정리한다.
  // 결제 화면은 메모리의 예약 결과와 예약 idx를 사용하므로 그대로 유지한다.
  // 다른 탭에서 진행/확인 중인 항차의 세션과 차단 기록은 보존한다.
  useEffect(() => {
    if (!hasReservationResult && !hasPaymentResult) return;
    const completedSessionKey = String(
      bookingPayload?.passengerInfo?.formDataInfo?.sessionkey || ''
    ).trim();
    if (!completedSessionKey) return;

    removeScopedBookingSessionArtifacts({
      cruiseId: cruise?.codeToCruiseId ?? null,
      scheduleId: idx,
      expectedSessionKey: completedSessionKey,
    });
  }, [
    hasReservationResult,
    hasPaymentResult,
    idx,
    cruise?.codeToCruiseId,
    bookingPayload?.passengerInfo?.formDataInfo?.sessionkey,
  ]);

  // ✅ 상세 데이터 먼저 호출 → 매핑해서 cruise 상태에 저장
  useEffect(() => {
    const fetchDetailData = async () => {
      try {
        const res = await apiGet('/schedule/get_cruise_schedule_detail.php', { schedule_id: idx });
        if (res?.result !== 'success') {
          setNotFound(true);
          return;
        }
        setDetailData(res.data);
        
        const mapped = mapDetailToCruise(res.data, idx);
        if (!mapped) {
          setNotFound(true);
          return;
        }
        setCruise(mapped);

      } catch (e) {
        console.error('상세 데이터 호출 실패:', e);
        setNotFound(true);
      }
    };
    if (idx) fetchDetailData();
  }, [idx]);

  useEffect(() => {
    sessionCleanupAlertShownRef.current = false;
  }, [cruise?.codeToCruiseId, idx]);

  // idx가 바뀔 때마다 세션에 저장된 홀드 정보를 초기화
  useEffect(() => {
    snapshotRestorePromptShownRef.current = false;
    setBasketHoldInfo(readBasketHoldInfo(idx));
  }, [idx]);

  // 다른 탭/컴포넌트에서 발생한 홀드 정보 갱신 이벤트를 수신
  useEffect(() => {
    const refreshHoldInfo = (event) => {
      const targetIdx = event?.detail?.idx;
      if (targetIdx == null || String(targetIdx) === String(idx)) {
        // 이벤트가 오면 세션에서 최신 홀드 정보를 다시 읽어온다
        setBasketHoldInfo(readBasketHoldInfo(idx));
      }
    };
    const handleStorage = () => {
      setBasketHoldInfo(readBasketHoldInfo(idx));
    };
    window.addEventListener('basketHoldUpdated', refreshHoldInfo);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('basketHoldUpdated', refreshHoldInfo);
      window.removeEventListener('storage', handleStorage);
    };
  }, [idx]);

  // 홀드 시간이 갱신되면 만료 알림 플래그를 초기화
  useEffect(() => {
    if (basketHoldInfo?.releaseAt) {
      basketHoldAlertShownRef.current = false; // 새로운 홀드 시간이 들어오면 알림 플래그 초기화
    }
  }, [basketHoldInfo?.releaseAt]);

  // 예약 페이지에 머무르는 동안 홀드 타이머를 매초 카운트다운
  useEffect(() => {
    if (sessionTimeLeftMs != null && Number(sessionTimeLeftMs) > 0) {
      sessionCleanupAlertShownRef.current = false;
    }
  }, [sessionTimeLeftMs]);

  useEffect(() => {
    if (shouldStopReservationTimers) {
      setBasketHoldCountdown(null);
      return undefined;
    }
    if (!basketHoldInfo?.releaseAt) {
      setBasketHoldCountdown(null);
      return undefined;
    }

    const releaseMs = new Date(basketHoldInfo.releaseAt).getTime();
    if (!Number.isFinite(releaseMs)) {
      setBasketHoldCountdown(null);
      return undefined;
    }

    const formatCountdown = () => {
      const diff = releaseMs - Date.now();
      if (diff <= 0) {
        setBasketHoldCountdown('00:00'); // 0초 이하로 내려가면 만료로 처리
        return;
      }
      const totalSeconds = Math.floor(diff / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const hh = String(hours).padStart(2, '0');
      const mm = String(minutes).padStart(2, '0');
      const ss = String(seconds).padStart(2, '0');
      setBasketHoldCountdown(hours > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`);
    };

    formatCountdown();
    const intervalId = window.setInterval(formatCountdown, 1000);
    return () => window.clearInterval(intervalId);
  }, [basketHoldInfo?.releaseAt, shouldStopReservationTimers]);

  // 홀드 타이머가 만료되면 관련 세션/스냅샷을 정리하고 2단계로 이동
  useEffect(() => {
    if (shouldStopReservationTimers) return;
    if (!basketHoldCountdown || basketHoldCountdown !== '00:00') return;
    if (idx == null) return;
    if (basketHoldAlertShownRef.current) return;
    basketHoldAlertShownRef.current = true;

    const scheduleId = idx;
    const clearBasketArtifacts = () => {
      try {
        const toRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (isBookingBasketCacheKeyForSchedule(key, scheduleId)) toRemove.push(key);
        }
        toRemove.forEach(key => sessionStorage.removeItem(key));
      } catch (_) { /* noop */ }
      try { sessionStorage.removeItem(`booking:basketHold:${scheduleId}`); } catch (_) { /* noop */ }
      try { sessionStorage.setItem(`booking:basketSkipFetch:${scheduleId}`, '1'); } catch (_) { /* noop */ }
      window.dispatchEvent(new CustomEvent('basketHoldUpdated', { detail: { idx: scheduleId } })); // 타이머 UI에 만료 사실 전파
    };

    const downgradeSnapshot = () => {
      try {
        const snapKey = `booking:snapshot:${scheduleId}`;
        const snapStr = sessionStorage.getItem(snapKey);
        if (!snapStr) return;
        const snap = JSON.parse(snapStr);
        const nextSnap = { ...snap, stepHint: 2, ts: Date.now() };
        delete nextSnap.cabinRef;
        sessionStorage.setItem(snapKey, JSON.stringify(nextSnap));
      } catch (_) { /* noop */ }
    };

    try {
      alert({
        title: '객실 홀드 시간이 만료되었습니다.',
        content: (
          <p>
            선택했던 객실의 홀드 시간이 종료되어<br/>
            선실 위치 선택 단계로 돌아갑니다.
          </p>
        ),
        action: async () => {
          clearBasketArtifacts();
          downgradeSnapshot();
          await restoreFromSnapshot({ skipStepSwitch: true, forceStep: 2, dropCabinSelection: true }); // 만료 후에도 스냅샷 로직을 재사용해 최신 정보 반영
          if (currentStep !== 2) {
            markStepChange('basketHoldExpired');
            replaceStepState(2);
            setCurrentStep(2);
          }
        },
        onClose: async () => {
          clearBasketArtifacts();
          downgradeSnapshot();
          await restoreFromSnapshot({ skipStepSwitch: true, forceStep: 2, dropCabinSelection: true }); // 알림 닫기만 해도 동일 처리
          if (currentStep !== 2) {
            markStepChange('basketHoldExpired');
            replaceStepState(2);
            setCurrentStep(2);
          }
        },
      });
    } catch (_) { /* noop */ }
    setBasketHoldInfo(null);
    setBasketHoldCountdown(null);
  }, [basketHoldCountdown, idx, currentStep, markStepChange, replaceStepState, restoreFromSnapshot, shouldStopReservationTimers]);

  useEffect(() => {
    if (!sessionCountdownLabel) {
      sessionIndicatorDragRef.current.isDragging = false;
      if (typeof document !== 'undefined') {
        document.body.style.userSelect = '';
      }
      return;
    }
    const node = sessionIndicatorRef.current;
    if (!node) return;
    const { offsetWidth, offsetHeight } = node;
    if (!offsetWidth || !offsetHeight) return;
    sessionIndicatorSizeRef.current = { width: offsetWidth, height: offsetHeight };
    setSessionIndicatorPosition((prev) => {
      const clamped = clampIndicatorPosition(prev);
      if (clamped.x === prev.x && clamped.y === prev.y) return prev;
      return clamped;
    });
  }, [sessionCountdownLabel, isSessionCountdownCritical]);

  useEffect(() => {
    if (loading) return;
    if (hasBookingSubmissionBlock) return;
    if (user) {
      enforcedLoginDowngradeRef.current = false;
      return;
    }
    if (currentStep < 3) return;
    if (enforcedLoginDowngradeRef.current) return;
    enforcedLoginDowngradeRef.current = true;
    const scheduleId = idx;
    // 비로그인 복원 시 기존 홀드/바스켓을 제거하고 스냅샷 단계도 강제로 2로 낮춘 뒤 다시 선실 단계로 되돌린다
    clearHoldArtifactsForSchedule(scheduleId);
    downgradeSnapshotToStepTwo(scheduleId);
    setBasketHoldInfo(null);
    setBasketHoldCountdown(null);
    // passenger / 결제 정보는 비회원에게 필요 없으므로 제거
    setBookingPayload(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next.passengerInfo; // 승객 입력 제거
      delete next.paymentResult; // 결제 결과 제거
      return next;
    });
    (async () => {
      try {
        await restoreFromSnapshot({ skipStepSwitch: true, forceStep: 2, dropCabinSelection: true });
      } finally {
        if (!isUnmountedRef.current) {
          markStepChange('loginRequiredDowngrade');
          replaceStepState(2);
          setCurrentStep(2);
        }
      }
    })();
  }, [
    loading,
    user,
    currentStep,
    idx,
    clearHoldArtifactsForSchedule,
    downgradeSnapshotToStepTwo,
    restoreFromSnapshot,
    replaceStepState,
    markStepChange,
    hasBookingSubmissionBlock,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updatePosition = (clientX, clientY) => {
      const dragState = sessionIndicatorDragRef.current;
      setSessionIndicatorPosition((prev) => {
        const next = clampIndicatorPosition({
          x: clientX - dragState.offsetX,
          y: clientY - dragState.offsetY,
        });
        if (next.x === prev.x && next.y === prev.y) return prev;
        return next;
      });
      document.body.style.userSelect = 'none';
    };

    const handleMouseMove = (event) => {
      if (!sessionIndicatorDragRef.current.isDragging) return;
      event.preventDefault();
      updatePosition(event.clientX, event.clientY);
    };

    const handleMouseUp = () => {
      if (!sessionIndicatorDragRef.current.isDragging) return;
      sessionIndicatorDragRef.current.isDragging = false;
      sessionIndicatorDragRef.current.pointerId = null;
      document.body.style.userSelect = '';
    };

    const handleTouchMove = (event) => {
      if (!sessionIndicatorDragRef.current.isDragging) return;
      const dragState = sessionIndicatorDragRef.current;
      const activeId = dragState.pointerId;
      let touch = null;
      if (activeId != null) {
        for (let i = 0; i < event.touches.length; i++) {
          const t = event.touches[i];
          if (t.identifier === activeId) {
            touch = t;
            break;
          }
        }
      } else {
        touch = event.touches[0];
      }
      if (!touch) return;
      event.preventDefault();
      updatePosition(touch.clientX, touch.clientY);
    };

    const handleTouchEnd = (event) => {
      if (!sessionIndicatorDragRef.current.isDragging) return;
      const dragState = sessionIndicatorDragRef.current;
      const activeId = dragState.pointerId;
      if (activeId == null) {
        dragState.isDragging = false;
        document.body.style.userSelect = '';
        return;
      }
      for (let i = 0; i < event.changedTouches.length; i++) {
        const t = event.changedTouches[i];
        if (t.identifier === activeId) {
          dragState.isDragging = false;
          dragState.pointerId = null;
          document.body.style.userSelect = '';
          break;
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
      document.body.style.userSelect = '';
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => {
      setSessionIndicatorPosition((prev) => {
        const clamped = clampIndicatorPosition(prev);
        if (clamped.x === prev.x && clamped.y === prev.y) return prev;
        return clamped;
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (shouldStopReservationTimers) {
      setSessionTimeLeftMs(null);
      return undefined;
    }
    if (!cruise) {
      setSessionTimeLeftMs(null);
      return undefined;
    }

    // 예약 페이지에 머무르는 동안만 TTL을 감시하고, 만료되면 관련 세션 키/캐시만 정리한다.
    const ttlMs = resolveSessionTtlMs();
    if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
      setSessionTimeLeftMs(null);
      return undefined;
    }

    const cruiseId = cruise?.codeToCruiseId ?? '';
    const scheduleId = idx;

    const computeRemainingMs = () => {
      try {
        let issuedAtStr = scheduleId != null
          ? sessionStorage.getItem(`booking:sessionKeyIssuedAt:schedule:${scheduleId}`)
          : null;
        if (!issuedAtStr && cruiseId) {
          issuedAtStr = sessionStorage.getItem(`booking:sessionKeyIssuedAt:${cruiseId}`);
        }
        const issuedAt = Number(issuedAtStr ?? NaN);
        if (!Number.isFinite(issuedAt) || issuedAt <= 0) return null;
        const remainingMs = (issuedAt + ttlMs) - Date.now();
        return remainingMs > 0 ? remainingMs : 0;
      } catch (_) {
        return null;
      }
    };

    /**
     * Ex)
     * - 48229 세션 발급 후 59065와 같이 다른 항차 예약진행중 48229 세션 만료시
     *  -이전에 보던 항차정보 관련 세션 스토리지 삭제
     */
    const cleanupExpiredSessionsForOthers = () => {
      if (typeof sessionStorage === 'undefined') return;
      const prefix = 'booking:sessionKeyIssuedAt:schedule:';
      const activeScheduleKey = scheduleId != null ? String(scheduleId) : null;
      const now = Date.now();
      const keysToCheck = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (!key || !key.startsWith(prefix)) continue;
        const scheduleKey = key.slice(prefix.length);
        if (!scheduleKey) continue;
        if (activeScheduleKey != null && scheduleKey === activeScheduleKey) continue;
        keysToCheck.push({ scheduleKey, storageKey: key });
      }

      keysToCheck.forEach(({ scheduleKey, storageKey }) => {
        try {
          if (hasStoredBookingSubmissionBlock(scheduleKey)) return;
          const issuedAtStr = sessionStorage.getItem(storageKey);
          const issuedAt = Number(issuedAtStr);
          if (!Number.isFinite(issuedAt) || issuedAt <= 0) return;
          if ((issuedAt + ttlMs) > now) return;
          // 활성 항차가 아니므로 알림 없이 세션 자료만 삭제
          removeBookingSessionArtifacts(null, scheduleKey);
        } catch (_) {
          // ignore issues while cleaning other sessions
        }
      });

      try {
        // booking:basketHold:* 키를 훑어 만료된 홀드를 조용히 정리한다
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (!key || !key.startsWith('booking:basketHold:')) continue;
          const scheduleKey = key.split(':').pop();
          if (!scheduleKey) continue;
          if (hasStoredBookingSubmissionBlock(scheduleKey)) continue;
          const holdDataStr = sessionStorage.getItem(key);
          if (!holdDataStr) continue;
          const holdData = JSON.parse(holdDataStr);
          const releaseAt = holdData?.releaseAt ? new Date(holdData.releaseAt).getTime() : NaN;
          if (!Number.isFinite(releaseAt)) continue;
          if (releaseAt > now) continue;
          const removable = [];
          for (let j = 0; j < sessionStorage.length; j++) {
            const basketKey = sessionStorage.key(j);
            if (isBookingBasketCacheKeyForSchedule(basketKey, scheduleKey)) removable.push(basketKey);
          }
          removable.forEach(basketKey => sessionStorage.removeItem(basketKey));
          sessionStorage.removeItem(`booking:basketHold:${scheduleKey}`);
          sessionStorage.removeItem(`booking:skipGroupsCache:${scheduleKey}`);
          window.dispatchEvent(new CustomEvent('basketHoldUpdated', { detail: { idx: scheduleKey } })); // 다른 컴포넌트에 만료된 홀드를 알려 UI 동기화
          const snapKey = `booking:snapshot:${scheduleKey}`;
          const snapStr = sessionStorage.getItem(snapKey);
          if (snapStr) {
            try {
              const snap = JSON.parse(snapStr);
              const nextSnap = { ...snap, stepHint: 2, ts: Date.now() };
              sessionStorage.setItem(snapKey, JSON.stringify(nextSnap));
            } catch (_) { /* noop */ }
          }
        }
      } catch (_) { /* noop */ }
    };

    const performSessionExpiryCleanup = () => {
      if (sessionCleanupAlertShownRef.current) return;
      sessionCleanupAlertShownRef.current = true;
      sessionIndicatorDragRef.current.isDragging = false;
      if (typeof document !== 'undefined') {
        document.body.style.userSelect = '';
      }
      try {
        removeBookingSessionArtifacts(cruiseId, scheduleId);
      } catch (_) { /* noop */ }

      if (!isUnmountedRef.current) {
        setSessionTimeLeftMs(null);
      }

      if (isUnmountedRef.current) return;

      try {
        alert({
          title: '세션 만료 안내',
          content: (
            <p>
              세션이 만료되었습니다.
              <br />
              다시 진행해 주세요.
            </p>
          ),
          onClose: () => {
            if (isUnmountedRef.current) return;
            // navigate('/');
            window.location.reload()
          },
        });
      } catch (_) {
        // alert 호출 실패 시 무시
      }
    };

    const syncCountdown = (options = {}) => {
      const remaining = computeRemainingMs();
      if (!isUnmountedRef.current) {
        setSessionTimeLeftMs(remaining);
      }
      if (options.forceCheck && remaining != null && remaining <= 0) {
        performSessionExpiryCleanup();
      }
      cleanupExpiredSessionsForOthers();
      return remaining;
    };

    const checkExpiryAndCleanup = () => {
      try {
        const remaining = syncCountdown();
        if (remaining == null || remaining > 0) return;
        performSessionExpiryCleanup();
      } catch (_) {
        // ignore runtime issues
      }
    };

    checkExpiryAndCleanup();
    cleanupExpiredSessionsForOthers();
    // 과도한 폴링을 피하되 TTL보다 훨씬 길게 놔두지 않도록 30초~2분 사이에서 주기 계산
    const intervalDelay = Math.max(30_000, Math.min(ttlMs / 3, 120_000));
    const expiryIntervalId = window.setInterval(checkExpiryAndCleanup, intervalDelay);
    const countdownIntervalId = window.setInterval(() => {
      if (isUnmountedRef.current) return;
      syncCountdown({ forceCheck: true });
    }, 1000);

    return () => {
      window.clearInterval(expiryIntervalId);
      window.clearInterval(countdownIntervalId);
    };
  }, [cruise, idx, navigate, shouldStopReservationTimers]);

  // 스냅샷 복구: step 기준으로 자동 복구 또는 초기화
  useEffect(() => {
    if (!cruise || !detailData) return;
    if (bookingPayload) return;

    const storageKey = `booking:snapshot:${idx}`;
  const rawSnap = sessionStorage.getItem(storageKey);
  if (!rawSnap) return;

  let snap;
  try {
      snap = JSON.parse(rawSnap);
    } catch (_) {
      sessionStorage.removeItem(storageKey);
      return;
    }

    // 제출 차단 중에는 만료 정리나 복원 취소가 실패 요청의 세션
    // 소유권을 없애지 않게 한다. 차단 안내를 표시할 수 있도록 기존
    // 스냅샷만 3단계로 복원하며 book.pl 재호출은 자식에서 차단한다.
    if (hasBookingSubmissionBlock) {
      snapshotRestorePromptShownRef.current = true;
      restoreFromSnapshot({ forceStep: 3, snapOverride: snap }).catch(err => {
        console.error('blocked booking snapshot restore failed', err);
      });
      return;
    }
    const basketHoldData = readBasketHoldInfo(idx);
    // 스냅샷 복원 시점에 객실 홀드가 만료되었는지 확인한다
    const releaseAtMs = basketHoldData?.releaseAt ? new Date(basketHoldData.releaseAt).getTime() : null;
    const holdExpired = Number.isFinite(releaseAtMs) && releaseAtMs <= Date.now();

    if (holdExpired) {
      try {
        const removable = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (isBookingBasketCacheKeyForSchedule(key, idx)) removable.push(key);
        }
        removable.forEach(key => sessionStorage.removeItem(key));
      } catch (_) { /* noop */ }
      try { sessionStorage.removeItem(`booking:basketHold:${idx}`); } catch (_) { /* noop */ }
      window.dispatchEvent(new CustomEvent('basketHoldUpdated', { detail: { idx } }));
      setBasketHoldInfo(null);
      setBasketHoldCountdown(null);
      const downgradedSnap = { ...snap, stepHint: 2, ts: Date.now() };
      delete downgradedSnap.cabinRef;
      snap = downgradedSnap;
      sessionStorage.setItem(storageKey, JSON.stringify(downgradedSnap));
    }

    const clearStoredBookingData = () => {
      try {
        const cruiseId = cruise?.codeToCruiseId ?? null;
        const removed = removeBookingSessionArtifacts(cruiseId, idx);
        try { sessionStorage.removeItem(storageKey); } catch (_) { /* noop */ }
        try { sessionStorage.removeItem(`booking:basketHold:${idx}`); } catch (_) { /* noop */ }
        try { sessionStorage.setItem(`booking:skipGroupsCache:${idx}`, '1'); } catch (_) { /* noop */ }
        window.dispatchEvent(new CustomEvent('basketHoldUpdated', { detail: { idx } }));
        if (!isUnmountedRef.current) setSessionTimeLeftMs(null);
        sessionCleanupAlertShownRef.current = false;
        return removed;
      } catch (_) {
        return false;
      }
    };

    const stepHintValue = Number(snap?.stepHint);
    const stepHint = Number.isFinite(stepHintValue) ? stepHintValue : 1;

    if (stepHint < 2) {
      clearStoredBookingData();
      try {
        if (!isUnmountedRef.current) {
          markStepChange('snapClear');
          replaceStepState(0);
          setCurrentStep(0);
        }
      } catch (_) { /* noop */ }
      return;
    }

    if (snapshotRestorePromptShownRef.current) return;
    snapshotRestorePromptShownRef.current = true; // 동일 세션에서 복원 안내가 중복 노출되지 않도록 가드

    setTimeout(() => {
      try {
        alert({
          title: '진행중인 예약정보 확인',
          content: <p>진행 중이던 예약 정보가 있습니다.<br/>이어서 진행 할까요?</p>,
          confirmButtonText: '취소',
          footerContent: <button type='button' className='btn'>확인</button>,
          onClose: () => {
            if (isUnmountedRef.current) return;
            clearStoredBookingData();
          },
          action: async () => {
            try {
              await restoreFromSnapshot({ snapOverride: snap });
            } catch (err) {
              console.error('restore snapshot failed', err);
            }
          },
        });
      } catch (_) {
        // ignore
      }
    }, 0);
  }, [cruise, detailData, bookingPayload, navigate, idx, reservationType, restoreFromSnapshot, hasBookingSubmissionBlock]);

  // ---------- 3) 에러 메시지 분기 ----------
  // 에러 처리 (기존 메시지 유지)
  let errorMessage = '';
  if (!idx) errorMessage = '상품 정보가 없습니다.';
  else if (notFound) errorMessage = '해당하는 크루즈 상품을 찾을 수 없습니다.';

  const labelMap = useMemo(() => ({
    adults: passengerAgeConfig.adult.summaryLabel,
    children: passengerAgeConfig.child.summaryLabel,
  }), [passengerAgeConfig]);

  if (errorMessage) {
    return (
      <div className='booking-section'>
        <div className="booking-wrapper">
          <div className='booking-header'>
            <h2 className='title'>예약하기</h2>
          </div>
          <div style={{ padding: "40px 0", textAlign: "center" }}>{errorMessage}</div>
        </div>
      </div>
    );
  }

  if (isLoading || !cruise) {
    // 기존 로딩 컴포넌트 사용
    return <Loading isLoading={true} />;
  }
  

  // 다음 단계 로직 depth 진행 (0~5)
  const handleNextStep = (payload, stepIncrement = 1) => {
    markStepChange('handleNextStep');
    let payloadForSnapshot = payload;
    if (payload) {
      const normalizedCounts = payload?.counts ? normalizeCounts(payload.counts) : null;
      const normalizedAges = payload?.ages ? normalizeAges(payload.ages) : null;
      const normalizedPastPax = normalizePastPax(payload?.pastPax);
      const normalizedMembershipPassengerInfo = normalizeMembershipPassengerInfo(payload?.membershipPassengerInfo);
      const enhancedPayload = payload && typeof payload === 'object'
        ? {
            ...payload,
            ...(normalizedCounts ? { counts: normalizedCounts } : {}),
            ...(normalizedAges ? { ages: normalizedAges } : {}),
            ...(Object.prototype.hasOwnProperty.call(payload, 'pastPax') ? { pastPax: normalizedPastPax } : {}),
            ...(Object.prototype.hasOwnProperty.call(payload, 'membershipPassengerInfo') ? { membershipPassengerInfo: normalizedMembershipPassengerInfo } : {}),
          }
        : payload;
      payloadForSnapshot = enhancedPayload;

      switch (currentStep) {
        case 0: {
          setBookingPayload(prev => {
            const preservedType = prev?.reservationType ?? false;
            const nextIncludeSenior =
              typeof enhancedPayload?.includeSenior === 'boolean'
                ? enhancedPayload.includeSenior
                : (typeof enhancedPayload?.seniorStatus === 'boolean'
                  ? enhancedPayload.seniorStatus
                  : null);
            const nextPayload = {
              cruise,
              detailData,
              reservationType: preservedType,
              counts: normalizedCounts ?? { adults: 0, children: 0 },
              ...(normalizedAges ? { ages: normalizedAges } : {}),
              includeSenior: nextIncludeSenior,
              ...(Object.prototype.hasOwnProperty.call(enhancedPayload || {}, 'pastPax') ? { pastPax: normalizedPastPax } : {}),
              ...(Object.prototype.hasOwnProperty.call(enhancedPayload || {}, 'membershipPassengerInfo') ? { membershipPassengerInfo: normalizedMembershipPassengerInfo } : {}),
            };
            return nextPayload;
          });
          break;
        }
        case 1:
          setBookingPayload(prev => {
            const isPaxRefreshOnly =
              enhancedPayload &&
              typeof enhancedPayload === 'object' &&
              Object.prototype.hasOwnProperty.call(enhancedPayload, 'pastPax') &&
              !Object.prototype.hasOwnProperty.call(enhancedPayload, 'carbinGradeInfo');
            const { userSelectCabinInfo, passengerInfo, paymentResult, seniorStatus, ...rest } = prev || {};
            const baseRest = { ...rest };
            if (isPaxRefreshOnly) {
              delete baseRest.carbinGradeInfo;
            }
            const base = {
              cruise,
              detailData,
              ...baseRest,
            };
            const next = { ...base, ...enhancedPayload };
            if (!('reservationType' in next)) {
              next.reservationType = prev?.reservationType ?? false;
            }
            return next;
          });
          break;
        case 2:
          setBookingPayload(prev => {
            const { passengerInfo, paymentResult, seniorStatus, ...rest } = prev || {};
            const next = { ...rest, ...enhancedPayload };
            if (!('reservationType' in next)) {
              next.reservationType = prev?.reservationType ?? false;
            }
            return next;
          });
          break;
        case 3:
          setBookingPayload(prev => {
            const hasReservationType = enhancedPayload && typeof enhancedPayload === 'object' && 'reservationType' in enhancedPayload;
            const nextReservationType = hasReservationType
              ? enhancedPayload.reservationType
              : prev?.reservationType ?? false;
            const passengerInfoPayload = enhancedPayload && typeof enhancedPayload === 'object' && 'passengerInfo' in enhancedPayload
              ? enhancedPayload.passengerInfo
              : enhancedPayload;
            const { seniorStatus, ...restPrev } = prev || {};
            const next = {
              ...restPrev,
              reservationType: nextReservationType,
              passengerInfo: passengerInfoPayload,
            };
            if (passengerInfoPayload?.bookingResult) {
              next.reservationCreated = true;
            }
            return next;
          });
          break;
        case 4:
          setBookingPayload(prev => {
            const { seniorStatus, ...restPrev } = prev || {};
            const next = {
              ...restPrev,
              ...(enhancedPayload && enhancedPayload.passengerInfo ? { passengerInfo: enhancedPayload.passengerInfo } : {}),
              paymentResult: enhancedPayload,
            };
            return next;
          });
          break;
        default:
          break;
      }
    }

    const desiredNextStep = Math.min(5, Math.max(0, currentStep + stepIncrement));
    const isMovingForwardFromCabinSelection = currentStep === 2 && stepIncrement > 0;
    const shouldRequireLogin = isMovingForwardFromCabinSelection && !loading && !user;
    const nextStep = shouldRequireLogin ? currentStep : desiredNextStep;

    const createdResult = payloadForSnapshot?.passengerInfo?.bookingResult || payloadForSnapshot?.bookingResult;
    if (createdResult) rememberCreatedBooking(idx, createdResult);
    // Once created, resume by reservation ID instead of recreating a passenger snapshot.
    if (!hasCreatedReservation) try {
      const prevSnapStr = sessionStorage.getItem(`booking:snapshot:${idx}`);
      const prevSnap = prevSnapStr ? JSON.parse(prevSnapStr) : {};
      const base = { stepHint: nextStep, reservationType, ts: Date.now() };
      let nextSnap = { ...prevSnap, ...base };
      const reservationCreatedForSnapshot = !!(
        payloadForSnapshot?.passengerInfo?.bookingResult ||
        payloadForSnapshot?.bookingResult
      );
      if (reservationCreatedForSnapshot) {
        nextSnap.reservationCreated = true;
        if (createdResult?.reservation_idx) nextSnap.createdReservationIdx = createdResult.reservation_idx;
      }

      if (payloadForSnapshot?.counts) {
        nextSnap.counts = payloadForSnapshot.counts;
        if (payloadForSnapshot?.ages) nextSnap.ages = payloadForSnapshot.ages;
      }

      if (payloadForSnapshot && Object.prototype.hasOwnProperty.call(payloadForSnapshot, 'includeSenior')) {
        nextSnap.includeSenior = payloadForSnapshot.includeSenior;
      } else if (payloadForSnapshot && Object.prototype.hasOwnProperty.call(payloadForSnapshot, 'seniorStatus')) {
        nextSnap.includeSenior = payloadForSnapshot.seniorStatus;
      }
      if (Object.prototype.hasOwnProperty.call(nextSnap, 'seniorStatus')) delete nextSnap.seniorStatus;

      if (payloadForSnapshot && Object.prototype.hasOwnProperty.call(payloadForSnapshot, 'pastPax')) {
        const nextPastPax = normalizePastPax(payloadForSnapshot.pastPax);
        if (nextPastPax) nextSnap.pastPax = nextPastPax;
        else delete nextSnap.pastPax;
      }
      if (payloadForSnapshot && Object.prototype.hasOwnProperty.call(payloadForSnapshot, 'membershipPassengerInfo')) {
        const nextMembershipPassengerInfo = normalizeMembershipPassengerInfo(payloadForSnapshot.membershipPassengerInfo);
        if (nextMembershipPassengerInfo) nextSnap.membershipPassengerInfo = nextMembershipPassengerInfo;
        else delete nextSnap.membershipPassengerInfo;
      }

      const isPaxRefreshOnly =
        currentStep === 1 &&
        payloadForSnapshot &&
        typeof payloadForSnapshot === 'object' &&
        Object.prototype.hasOwnProperty.call(payloadForSnapshot, 'pastPax') &&
        !Object.prototype.hasOwnProperty.call(payloadForSnapshot, 'carbinGradeInfo');
      if (isPaxRefreshOnly) {
        delete nextSnap.selectedRef;
        delete nextSnap.cabinRef;
      }

      if (currentStep === 1 && payloadForSnapshot?.carbinGradeInfo) {
        const gi = payloadForSnapshot.carbinGradeInfo;
        const gp = gi?.gridPrice || {};
        const gm = gi?.gridMeta || {};
        const selectedRef = {
          gradeno: gp.gradeno ?? gp?.rate?.gradeno ?? gm.gradeno ?? gi?.gradeno ?? null,
          ratecode: gp?.ratecode ?? gp?.rate?.code ?? gm.ratecode ?? null,
          resultno: resolveTraveltekResultNo(
            gi?.resultno ?? gp?.resultno ?? gm.resultno ?? null,
            gp.gradeno ?? gp?.rate?.gradeno ?? gm.gradeno ?? gi?.grade_no ?? gi?.gradeno ?? null,
          ),
          deckImageUrl: gi?.deckImageUrl ?? gi?.image_url ?? null,
          grade_name: gi?.grade_name ?? null,
          grade_name_ko: gi?.grade_name_ko ?? null,
          cabincode: gi?.cabincode ?? null,
          cabinMeta: gi?.cabinMeta ?? null,
        };
        nextSnap.selectedRef = selectedRef;
      }

      if (currentStep === 2 && payloadForSnapshot?.userSelectCabinInfo) {
        const c = payloadForSnapshot.userSelectCabinInfo;
        nextSnap.cabinRef = {
          cabinno: c.cabinno,
          location: c.location,
          deckname: c.deckname,
          resultno: c.resultno,
        };
      }

      sessionStorage.setItem(`booking:snapshot:${idx}`, JSON.stringify(nextSnap));
    } catch (_) { /* noop */ }

    if (shouldRequireLogin) {
      const redirectUrl = `${location.pathname}${location.search || ''}${location.hash || ''}`;
      alert({
        title: '로그인이 필요한 서비스 입니다.',
        content: (
          <p>
            예약을 진행하려면 로그인이 필요합니다.<br />로그인 페이지로 이동할까요?
          </p>
        ),
        confirmButtonText: '취소',
        footerContent: <button type='button' className='btn'>로그인</button>,
        action: () => {
          navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
        },
      });
      return;
    }

    markStepChange('handleNextStepNavigate');
    pushStepState(nextStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBookingFailureReset = (resetRequest = {}) => {
    const cruiseId = cruise?.codeToCruiseId ?? null;
    const blockKey = idx ? `booking:submissionBlocked:${idx}` : null;
    if (!blockKey || typeof sessionStorage === 'undefined') {
      return { ok: false, reason: 'MISSING_RESET_CONTEXT' };
    }

    let storedBlock;
    try {
      storedBlock = parseBookingSubmissionBlock(sessionStorage.getItem(blockKey));
    } catch (_) {
      storedBlock = null;
    }
    const expectedSessionKey = String(resetRequest?.expectedSessionKey || '');
    const storedExpectedSessionKey = String(storedBlock?.expectedSessionKey || '');
    const requestIdMatches = String(resetRequest?.requestId || '')
      === String(storedBlock?.requestId || '');
    const scheduleIdMatches = storedBlock?.scheduleId == null
      || String(storedBlock.scheduleId) === String(idx);
    if (resetRequest?.policy !== BOOKING_SUBMISSION_POLICY.RESTART_SAFE
      || storedBlock?.policy !== BOOKING_SUBMISSION_POLICY.RESTART_SAFE
      || !expectedSessionKey
      || expectedSessionKey !== storedExpectedSessionKey
      || !scheduleIdMatches
      || !requestIdMatches) {
      return { ok: false, reason: 'UNSAFE_BOOKING_RESET_REQUEST' };
    }

    const reselectCabin = storedBlock.reason === 'cabin_reselection_required';
    const reselectionPayload = reselectCabin
      ? buildCabinReselectionPayload(bookingPayload, cruise, detailData) : null;
    if (reselectCabin && (currentStep !== 3 || !reselectionPayload)) {
      return { ok: false, reason: 'INVALID_CABIN_RESELECTION_CONTEXT' };
    }

    const cleanupResult = removeScopedBookingSessionArtifacts({
      cruiseId,
      scheduleId: idx,
      expectedSessionKey,
    });
    if (!cleanupResult.ok) return cleanupResult;

    // 결과 불확정 차단은 이 경로로 들어올 수 없다. 확정 실패 차단키도
    // 예약 자료 정리가 검증된 뒤 마지막으로 해제한다.
    try {
      sessionStorage.removeItem(blockKey);
      if (sessionStorage.getItem(blockKey) !== null) {
        return { ok: false, reason: 'BOOKING_BLOCK_CLEANUP_INCOMPLETE' };
      }
    } catch (_) {
      return { ok: false, reason: 'BOOKING_BLOCK_CLEANUP_FAILED' };
    }
    handleBookingSubmissionBlockChange();

    try {
      window.dispatchEvent(new CustomEvent('basketHoldUpdated', { detail: { idx } }));
    } catch (_) { /* noop */ }

    basketHoldAlertShownRef.current = false;
    snapshotRestorePromptShownRef.current = false;
    sessionCleanupAlertShownRef.current = false;
    enforcedLoginDowngradeRef.current = false;

    setIsRestoring(false);
    setBookingPayload(reselectionPayload);
    setBasketHoldInfo(null);
    setBasketHoldCountdown(null);
    setSessionTimeLeftMs(null);

    markStepChange('bookingFailureReset');
    replaceStepState(reselectCabin ? 1 : 0);
    setCurrentStep(reselectCabin ? 1 : 0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return { ok: true };
  };

  const handleSessionIndicatorMouseDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const dragState = sessionIndicatorDragRef.current;
    dragState.isDragging = true;
    dragState.offsetX = event.clientX - sessionIndicatorPosition.x;
    dragState.offsetY = event.clientY - sessionIndicatorPosition.y;
    dragState.pointerId = null;
  };

  const handleSessionIndicatorTouchStart = (event) => {
    if (!event.touches || event.touches.length === 0) return;
    const touch = event.touches[0];
    const dragState = sessionIndicatorDragRef.current;
    dragState.isDragging = true;
    dragState.offsetX = touch.clientX - sessionIndicatorPosition.x;
    dragState.offsetY = touch.clientY - sessionIndicatorPosition.y;
    dragState.pointerId = touch.identifier;
  };

  const cabinMeta = bookingPayload?.carbinGradeInfo?.cabinMeta || null;
  const cabinInfoItems = cabinMeta
    ? [
        { label: '최대', value: cabinMeta.maxGuest },
        { label: '선실', value: cabinMeta.cabinSize },
        { label: '발코니', value: cabinMeta.balconySize },
      ].filter(item => item.value)
    : [];
  const cabinDescItems = cabinMeta
    ? [
        { label: '침대', value: cabinMeta.bed },
        { label: '욕실', value: cabinMeta.bath },
        { label: '특징', value: cabinMeta.feature },
      ].filter(item => item.value)
    : [];
  const appliedMembershipNo = normalizePastPax(bookingPayload?.pastPax);

  return (
    <div className='booking-section'>
      {isDisneyBooking && <MainNoticePopup storageScope="bookingPopup" />}
      {!shouldStopReservationTimers && (sessionCountdownLabel || basketHoldCountdown) && (
        <div
          className="session-expiry-wrapper"
          style={{
            left: `${sessionIndicatorPosition.x}px`,
            top: `${sessionIndicatorPosition.y}px`,
          }}
          onMouseDown={handleSessionIndicatorMouseDown}
          onTouchStart={handleSessionIndicatorTouchStart}
          role="presentation"
        >
          {/* 상단 세션/홀드 타이머를 한 번에 움직일 수 있도록 공용 래퍼에서 가로 배치 */}
          {sessionCountdownLabel && (
            <div
              ref={sessionIndicatorRef}
              className={`session-expiry-indicator session-expiry-indicator-floating${isSessionCountdownCritical ? ' is-critical' : ''}${isSessionExpired ? ' is-expired' : ''}`}
              role="status"
              aria-live="polite"
            >
              {isSessionExpired ? '세션 만료됨' : (<><span>세션 만료까지</span> <span>{sessionCountdownLabel}</span></>)}
            </div>
          )}
          {basketHoldCountdown && (
            <div
              className={`session-expiry-indicator session-expiry-indicator-floating secondary${basketHoldCountdown === '00:00' ? ' is-expired' : ''}${isHoldCountdownCritical ? ' is-critical' : ''}`}
              role="status"
              aria-live="polite"
            >
              {basketHoldCountdown === '00:00'
                ? '객실 홀드 만료됨'
                : (<><span>객실 홀드 만료까지</span> <span>{basketHoldCountdown}</span></>)}
            </div>
          )}
        </div>
      )}
      <div className="booking-wrapper">
        {/* 복구시 로딩 처리 */}
        {isRestoring && <Loading isLoading={true} />}
        <div className='booking-header'>
          <h2 className='title'>예약하기</h2>
          <div className="booking-depth">
            {headerGroups.map((group, i) => {
              const isActive = group.steps.includes(currentStep);
              const earliestStep = Math.min(...group.steps);
              const canNavigateTo = (() => {
                if (currentStep === 5) return false;
                if (hasBookingSubmissionBlock) return false;
                if (isActive) return false;
                if (earliestStep > currentStep) return false;
                if (shouldLockPaymentStep && earliestStep < 4) return false;
                return true;
              })();
              const displayStep = group.steps[0];
              const number = group.number;
              const name = group.name;

              return (
                <div
                  key={i}
                  className={`depth ${isActive ? 'on' : ''}`}
                  onClick={() => {
                    if (!canNavigateTo) return;
                    try {
                      const eligible = group.steps.filter(s => s <= currentStep);
                      const target = eligible.length > 0 ? Math.max(...eligible) : displayStep;
                      const key = `booking:snapshot:${idx}`;
                      const prevStr = sessionStorage.getItem(key);
                      const prev = prevStr ? JSON.parse(prevStr) : {};
                      const nextSnap = { ...prev, stepHint: target, reservationType, ts: Date.now() };
                      sessionStorage.setItem(key, JSON.stringify(nextSnap));
                      markStepChange('headerClick');
                      pushStepState(target);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } catch (_) { /* noop */ }
                  }}
                  role="button"
                  title={canNavigateTo ? `${name}로 이동` : undefined}
                  aria-disabled={!canNavigateTo}
                  style={{ cursor: canNavigateTo ? 'pointer' : 'default'}}
                >
                  <div className='depth-value'>
                    <span className="step-number">{number}</span>
                    <span className="step-name"> {name}</span>
                  </div>
                  {i !== headerGroups.length - 1 && <ArrowSVG className="arrow" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* depth 1 && 3 && 4 header */}
        {currentStep !== 2 && currentStep !== 5 && cruise && (
          <div className='booking-thumbnail'>
            <img src={cruise.image} alt={cruise.shipName} className='booking-thumbnail-image' />
            <div className='booking-thumbnail-info'>
              <div className='info'>
                <h2 className='title'>{cruise.title}</h2>
                <div className='desc'>
                  <div className='code'>
                    <span>항차 코드</span>
                    <span>{cruise.code}</span>
                  </div>
                  <div className='divider'></div>
                  {/* 상세 일정 모달은 기존대로 */}
                  <CommonModal
                    trigger={<button type='button' className="detail-info-modal">상세 일정 보기 ＞</button>}
                    title="상세 일정 보기"
                    showFooter={false}
                  >
                    {detailData && (
                      <NavScheduleContent
                        simple={[
                          { date: detailData.schedule.saildate, type: 'departure', location: detailData.schedule.departure_port_name },
                          { date: detailData.schedule.enddate, type: 'arrival', location: detailData.schedule.arrival_port_name },
                        ]}
                        map={detailData.schedule.itinerary_image_url}
                        detail={detailData.itinerary}
                        forceMobile={true}
                      />
                    )}
                  </CommonModal>
                </div>
              </div>

              <div className='info-detail'>
                {['출발','도착'].map((label, i) => {
                  const dateKey = i === 0 ? 'departureDate' : 'arrivalDate';
                  const timeKey = i === 0 ? 'departureTime' : 'arrivalTime';
                  const locKey  = i === 0 ? 'departureLocation' : 'arrivalLocation';
                  const dt = formatBookingCalendarDate(cruise[dateKey]);
                  const tm = cruise[timeKey] || '';
                  return (
                    <div key={i} className="cruise-time-row">
                      <span className="cruise-time-label label">{label}</span>
                      <div className="cruise-time-value">
                        <div className='cruise-time-value-date'>
                          <span className="cruise-time-date">{dt}</span>
                          <span className="cruise-time-time">{tm}</span>
                        </div>
                        <span className="cruise-time-location">
                          <LocationSVG />{cruise[locKey] || '-'}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {appliedMembershipNo && (
                  <div className="booking-membership-summary">
                    <span className="booking-membership-summary-label">적용된 회원번호</span>
                    <strong>{appliedMembershipNo}</strong>
                  </div>
                )}

                {currentStep < 2 && (
                  <div className='info-detail-price'>
                    <span className='info-detail-price-label'>2인 1실 기준,<br/>1인 최저 요금</span>
                    <div className="info-detail-price-value">
                      {cruise.roomType?.length > 0 ? (
                        cruise.roomType.map((item, idx) => (
                          <div className="price-row" key={idx}>
                            <span>{item.typeName}</span>
                            <span>
                              {Number((item.lowPrice / 2)).toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                              })}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="price-row">
                          <span>가격 정보가 없습니다.</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* depth 2 전용 header */}
        {currentStep === 2 && bookingPayload && (
          <div className='depth-2-booking-thumbnail'>
            <div className='header'>
              <h2 className='title'>{cruise.title}</h2>
              <div className='desc'>
                <div className='code'>
                  <span>항차 코드</span>
                  <span>{cruise.code}</span>
                </div>
                <div className='divider'></div>
                {/* 상세일정 모달 */}
               <CommonModal
                  trigger={<button type='button' className="detail-info-modal depth-2">상세 일정 보기 ＞</button>}
                  title="상세 일정 보기"
                  showFooter={false}
                >
                  {detailData && (
                    <NavScheduleContent
                      simple={[
                        { date: detailData.schedule.saildate, type: 'departure', location: detailData.schedule.departure_port_name },
                        { date: detailData.schedule.enddate, type: 'arrival', location: detailData.schedule.arrival_port_name },
                      ]}
                      map={detailData.schedule.itinerary_image_url}
                      detail={detailData.itinerary}
                      forceMobile={true}
                    />
                  )}
                </CommonModal>
              </div>
            </div>

            <div className='body'>
              <img
                src={bookingPayload.carbinGradeInfo.deckImageUrl}
                alt={bookingPayload.carbinGradeInfo.grade_name_ko || bookingPayload.carbinGradeInfo.grade_name}
                className='booking-room-image'
              />
              <div className='room-details'>
                <h3 className='room-name'>
                  {bookingPayload.carbinGradeInfo.grade_name_ko || bookingPayload.carbinGradeInfo.grade_name}
                </h3>
                {cabinInfoItems.length > 0 && (
                  <div className='room-info'>
                    {cabinInfoItems.reduce((acc, item, idx, arr) => {
                      acc.push(
                        <div key={item.label}>
                          <span>{item.label} </span><span>{item.value}</span>
                        </div>
                      );
                      if (idx < arr.length - 1) {
                        acc.push(<div key={`divider-${item.label}`} className='divider'></div>);
                      }
                      return acc;
                    }, [])}
                  </div>
                )}

                {cabinDescItems.length > 0 && (
                  <div className='room-desc'>
                    {cabinDescItems.map(item => (
                      <div key={item.label} className='room-desc-info'>
                        <span>{item.label} </span><span>{item.value}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="room-booking-count-value">
                  {['adults', 'children']
                    .map((key) => ({ key, value: Number(bookingPayload?.counts?.[key]) || 0 }))
                    .filter(({ value }) => value > 0)
                    .reduce((acc, { key, value }, idx, arr) => {
                      acc.push(
                        <div key={key} className='value'>
                          <span>{labelMap[key] ?? key}</span>
                          <span>{value}</span>
                        </div>
                      );
                      if (idx < arr.length - 1) {
                        acc.push(<div key={`sep-${idx}`} className='divider'></div>);
                      }
                      return acc;
                    }, [])}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* depth 별 컨텐츠 */}
        <div className='booking-depth-flow'>
          {currentStep <= 1 && (
            <GuestRoomSelector
              stage={currentStep}
              cruise={cruise}
              scheduleId={idx}
              savedCounts={bookingPayload?.counts}
              savedAges={bookingPayload?.ages}
              savedPastPax={bookingPayload?.pastPax}
              savedMembershipPassengerInfo={bookingPayload?.membershipPassengerInfo}
              savedIncludeSenior={
                typeof bookingPayload?.includeSenior === 'boolean'
                  ? bookingPayload.includeSenior
                  : (typeof bookingPayload?.seniorStatus === 'boolean' ? bookingPayload.seniorStatus : null)
              }
              ageConfig={passengerAgeConfig}
              onSubmitPax={handleNextStep}
              onSyncPax={(payload) => handleNextStep(payload, 0)}
              onResetPax={() => handleNextStep(null, -1)}
              onNextStep={handleNextStep}
            />
          )}

          {currentStep === 2 && (
            <GuestRoomBook
              roomDetail={bookingPayload}
              onNextStep={handleNextStep}
            />
          )}

          {currentStep === 3 && (
            <GuestWriteInfo
              key={String(idx)}
              bookingPayload={bookingPayload}
              onNextStep={handleNextStep}
              onResetBookingFlow={handleBookingFailureReset}
              onBookingSubmissionBlockChange={handleBookingSubmissionBlockChange}
              userEmail={user?.id || ''}
              ageConfig={passengerAgeConfig}
              isRestoring={isRestoring} // 복원 상황인지 전달해 add_cruise_basket 재호출 시 hold 시간이 늘어나지 않도록 제어
            />
          )}
          {currentStep === 4 && (
            <GuestPayment 
              bookingPayload={bookingPayload}
              onNextStep={handleNextStep}
              ageConfig={passengerAgeConfig}
            />
          )}

          {currentStep === 5 && (
            <GuestBookingResult 
              bookingPayload={bookingPayload}
              ageConfig={passengerAgeConfig}
              
            />
          )}
        </div>

      </div>
    </div>
  );
}
